import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateConfigPipe } from '../pipes/translate-config.pipe';

@Component({
    selector: 'platform-count-dropdown',
    standalone: true,
    imports: [TranslateConfigPipe, TooltipModule],
    template: `
        <div class="count-dropdown-container" (mouseenter)="showDropdown = true" (mouseleave)="showDropdown = false">
            <span class="count-badge align-items-center cursor-pointer">
                <span class="flex align-items-center justify-content-center text-color font-semibold text-base">
                    +{{ count }}
                </span>
            </span>

            @if (showDropdown) {
                <div class="dropdown-list-wrapper">
                    <div class="dropdown-arrow"></div>

                    <div class="dropdown-list">
                        @for (item of items; track i; let i = $index) {
                            <div
                                class="dropdown-item flex justify-content-between align-items-center padding-px-16 padding-py-12 text-sm"
                                [pTooltip]="item.value | translateConfig"
                                tooltipPosition="right"
                                [tooltipOptions]="{ showDelay: 300 }"
                            >
                                <span class="dropdown-text">
                                    {{ item.value | translateConfig }}
                                </span>

                                <span
                                    class="remove-button flex align-items-center justify-content-center"
                                    (click)="onRemoveChip(i)"
                                >
                                    <i class="pi pi-times"></i>
                                </span>
                            </div>
                        }
                    </div>
                </div>
            }
        </div>
    `,
    styles: [
        `
            :host {
                display: inline-block;
            }

            .count-dropdown-container {
                position: relative;
                display: inline-block;
            }

            .count-badge {
                width: 2.5rem;
                height: 2.5rem;
                border: 2px solid var(--alpha-secondary-color);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0;
                cursor: pointer;
                transition: all 0.2s ease;
            }

            .count-badge:hover {
                background-color: var(--alpha-background-surface-hover-color);
            }

            .dropdown-list-wrapper {
                position: absolute;
                top: calc(100% + 12px);
                left: 50%;
                transform: translateX(-50%);
                z-index: 1000;
                animation: fadeIn 0.2s ease;
            }

            .dropdown-arrow {
                position: absolute;
                top: -10px;
                left: 50%;
                transform: translateX(-50%);
                width: 0;
                height: 0;
                border-left: 11px solid transparent;
                border-right: 11px solid transparent;
                border-bottom: 11px solid var(--alpha-primary-inactive-color);
            }

            .dropdown-arrow::after {
                content: '';
                position: absolute;
                top: 1px;
                left: 50%;
                transform: translateX(-50%);
                width: 0;
                height: 0;

                border-left: 10px solid transparent;
                border-right: 10px solid transparent;
                border-bottom: 10px solid var(--alpha-background-surface-color);
            }
            .dropdown-list {
                background: var(--alpha-background-surface-color);
                border-radius: 8px;
                min-width: 250px;
                max-height: 280px;
                overflow-y: auto;
                padding: 6px 0;
                border: 1px solid var(--alpha-primary-inactive-color);
            }

            .dropdown-item {
                padding: 14px 20px;
                cursor: pointer;
                background: var(--alpha-background-surface-color);
                transition: background-color 0.15s ease;
                justify-content: space-between;
                border-bottom: 1px solid var(--alpha-background-surface-hover-color);
            }

            .dropdown-item:hover {
                background-color: var(--alpha-background-surface-hover-color);
            }

            .dropdown-text {
                max-width: 220px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                font-weight: 400;
                font-size: var(--text-sm, 0.875rem);
                color: var(--alpha-text-color);
            }

            @keyframes fadeIn {
                from {
                    opacity: 0;
                    transform: translateX(-50%) translateY(-8px);
                }
                to {
                    opacity: 1;
                    transform: translateX(-50%) translateY(0);
                }
            }

            .remove-button {
                width: 1.2rem;
                height: 1.2rem;
                background: var(--alpha-secondary-fill-color);
                border-radius: 50%;
                cursor: pointer;
                opacity: 0;
                pointer-events: none;
                transition: opacity 0.2s ease;
            }

            .dropdown-item:hover .remove-button {
                opacity: 1;
                pointer-events: auto;
            }

            .remove-button i {
                font-size: 0.7rem;
                color: var(--text-color);
            }

            /* Scrollbar styles */
            .dropdown-list::-webkit-scrollbar {
                width: 6px;
            }

            .dropdown-list::-webkit-scrollbar-track {
                background: var(--alpha-background-surface-color);
                border-radius: 4px;
            }

            .dropdown-list::-webkit-scrollbar-thumb {
                background: var(--alpha-toggle-background-color);
                border-radius: 4px;
            }

            .dropdown-list::-webkit-scrollbar-thumb:hover {
                background: var(--alpha-background-surface-hover-color);
            }
        `,
    ],
})
export class PlatformCountDropdownComponent implements OnInit {
    @Input() label: string = '';
    @Input() items: FilterChip[] = [];
    @Input() count: number = 0;
    @Input() removable: boolean = true;
    @Output() onRemove = new EventEmitter<number>();

    showDropdown = false;

    ngOnInit(): void {
        if (this.count === 0 && this.items.length > 0) {
            this.count = this.items.length;
        }
    }

    onRemoveChip(index: number) {
        this.onRemove.emit(index);
    }
}

interface FilterChip {
    value: string;
    key: string;
    keys: string[];
    type: string;
}
